(() => {
  const desktopMediaQuery = window.matchMedia('(min-width: 990px)');
  const menuSelector = '[data-okg-mega-menu]';
  const topLevelMenuSelector = ':scope > .list-menu--inline > li > header-menu';
  const summarySelector = 'header-menu > details > summary.header__menu-item';
  const initializedAttribute = 'data-okg-mega-menu-ready';
  const productCardSelector = '.product-card-wrapper';
  const productCardHoverClass = 'is-okg-hover';

  const closeDetails = (details) => {
    details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  };

  const closeMenu = (menu) => {
    const details = menu.querySelector(':scope > details');
    if (details) closeDetails(details);
  };

  const getTopLevelMenus = (nav) => Array.from(nav.querySelectorAll(topLevelMenuSelector));

  const alignMenuColumns = (details, summary) => {
    if (!desktopMediaQuery.matches || !details || !summary) return;

    const list = details.querySelector(
      '.mega-menu__list--with-category-promo, .mega-menu__list--collections, .mega-menu__list--custom'
    );
    if (!list) return;

    const isCompactMenu = list.matches('.mega-menu__list--collections, .mega-menu__list--custom');
    if (isCompactMenu) {
      list.style.removeProperty('--okg-compact-menu-left');

      const columns = Array.from(list.children).filter(
        (item) =>
          !item.classList.contains('mega-menu__collections-gift-promo') &&
          !item.classList.contains('mega-menu__custom-visit-promo')
      );
      const firstColumn = columns[0];
      const lastColumn = columns.at(-1);
      if (!firstColumn || !lastColumn) return;

      const safeLeft = parseFloat(getComputedStyle(list).paddingLeft) || 0;
      const trigger = summary.getBoundingClientRect();
      const firstColumnBounds = firstColumn.getBoundingClientRect();
      const lastColumnBounds = lastColumn.getBoundingClientRect();
      const groupCenter = (firstColumnBounds.left + lastColumnBounds.right) / 2;
      const triggerCenter = (trigger.left + trigger.right) / 2;
      const centeredLeft = Math.max(safeLeft, Math.round(safeLeft + triggerCenter - groupCenter));

      list.style.setProperty('--okg-compact-menu-left', `${centeredLeft}px`);
      return;
    }

    const listLeft = list.getBoundingClientRect().left;
    const triggerLeft = Math.max(0, Math.round(summary.getBoundingClientRect().left - listLeft));
    const columnCount = Number(
      Array.from(list.classList)
        .find((className) => className.startsWith('mega-menu__list--columns-'))
        ?.replace('mega-menu__list--columns-', '')
    );

    list.style.setProperty('--okg-mega-menu-trigger-left', `${triggerLeft}px`);

    if (columnCount === 4) {
      const logo = summary.closest('header')?.querySelector('.header__heading-link');
      const logoLeft = logo
        ? Math.max(0, Math.round(logo.getBoundingClientRect().left - listLeft))
        : triggerLeft;

      list.style.setProperty('--okg-mega-menu-trigger-left', `${logoLeft}px`);
      return;
    }

    if (!columnCount || columnCount > 3) return;

    // Center compact submenu groups below their top-level navigation link.
    const columns = Array.from(list.children)
      .filter((item) => !item.classList.contains('mega-menu__category-promo'))
      .slice(0, columnCount);
    const firstColumn = columns[0];
    const lastColumn = columns.at(-1);
    if (!firstColumn || !lastColumn) return;

    const trigger = summary.getBoundingClientRect();
    const firstColumnBounds = firstColumn.getBoundingClientRect();
    const lastColumnBounds = lastColumn.getBoundingClientRect();
    const groupCenter = (firstColumnBounds.left + lastColumnBounds.right) / 2;
    const triggerCenter = (trigger.left + trigger.right) / 2;
    const centeredLeft = Math.max(0, Math.round(triggerLeft + triggerCenter - groupCenter));

    list.style.setProperty('--okg-mega-menu-trigger-left', `${centeredLeft}px`);
  };

  const openMenu = (menu) => {
    const details = menu.querySelector(':scope > details');
    const summary = details?.querySelector(':scope > summary');
    if (!details || !summary) return;

    getTopLevelMenus(menu.closest(menuSelector)).forEach((siblingMenu) => {
      if (siblingMenu !== menu) closeMenu(siblingMenu);
    });

    alignMenuColumns(details, summary);
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
  };

  const isTopLevelMenu = (nav, menu) => menu?.parentElement?.parentElement === nav.querySelector(':scope > .list-menu--inline');

  const bindMenu = (nav) => {
    if (nav.hasAttribute(initializedAttribute)) return;

    nav.setAttribute(initializedAttribute, 'true');

    getTopLevelMenus(nav).forEach((menu) => {
      const details = menu.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary');

      details?.addEventListener('toggle', () => {
        if (details.open) alignMenuColumns(details, summary);
      });
    });

    const handleHover = (event) => {
      if (!desktopMediaQuery.matches) return;

      const summary = event.target.closest(summarySelector);
      const menu = summary?.closest('header-menu');
      if (!summary || !isTopLevelMenu(nav, menu)) return;

      openMenu(menu);
    };

    nav.addEventListener('pointerover', handleHover);
    nav.addEventListener('mouseover', handleHover);

    nav.addEventListener('focusin', (event) => {
      if (!desktopMediaQuery.matches) return;

      const summary = event.target.closest(summarySelector);
      const menu = summary?.closest('header-menu');
      if (!summary || !isTopLevelMenu(nav, menu)) return;

      openMenu(menu);
    });

    nav.addEventListener('mouseleave', () => {
      if (!desktopMediaQuery.matches) return;

      window.setTimeout(() => {
        if (!nav.matches(':hover')) getTopLevelMenus(nav).forEach(closeMenu);
      }, 120);
    });
  };

  const initProductCardHover = () => {
    document.addEventListener('pointerover', (event) => {
      if (!desktopMediaQuery.matches) return;

      event.target.closest(productCardSelector)?.classList.add(productCardHoverClass);
    });

    document.addEventListener('pointerout', (event) => {
      if (!desktopMediaQuery.matches) return;

      const card = event.target.closest(productCardSelector);
      if (!card || card.contains(event.relatedTarget)) return;

      card.classList.remove(productCardHoverClass);
    });
  };

  const init = () => document.querySelectorAll(menuSelector).forEach(bindMenu);

  const alignOpenMenus = () => {
    if (!desktopMediaQuery.matches) return;

    document.querySelectorAll(`${menuSelector} > .list-menu--inline > li > header-menu > details[open]`).forEach((details) => {
      alignMenuColumns(details, details.querySelector(':scope > summary'));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('resize', alignOpenMenus);
  initProductCardHover();
})();
